function isPlayerPresentInRoster(playerId, roster) {
  if (playerId == null || !Array.isArray(roster)) return false;
  const pid = String(playerId);
  return roster.some((p) => {
    const present =
      p?.presensee === true ||
      p?.presensee === 1 ||
      p?.presensee === "1" ||
      p?.presensee === "true";
    return present && p.player_id != null && String(p.player_id) === pid;
  });
}

/** Resolve session coach user_id for player → coach feedback. */
export function resolveSessionCoachUserId(session) {
  const coaches = session?.coaches;
  if (!Array.isArray(coaches) || coaches.length === 0) return null;
  const withUser = coaches.find((c) => c?.user_id != null);
  return withUser?.user_id ?? null;
}

/** Trim string fields; empty/whitespace → "". */
export function trimPlayerText(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

/** Alias is optional; ignore null, empty, or whitespace-only values. */
export function isValidPlayerAlias(alias) {
  const trimmed = trimPlayerText(alias);
  return trimmed.length > 0 ? trimmed : null;
}

/** Full name when no alias: "Last First" if both exist, else whichever is set. */
export function formatPlayerFullName(p) {
  if (!p) return null;
  const first = trimPlayerText(p.user_first_name);
  const last = trimPlayerText(p.user_last_name);
  if (last && first) return `${last} ${first}`;
  if (last || first) return last || first;
  return null;
}

export function playerDisplayName(p) {
  if (!p) return null;
  return (
    isValidPlayerAlias(p.user_alias) ||
    formatPlayerFullName(p) ||
    trimPlayerText(p.player_name) ||
    null
  );
}

/** Presence/roster UI: alias, else "First Last", else whichever name is set. */
export function playerPresenceDisplayName(p, fallback = null) {
  if (!p || typeof p !== "object") return fallback;
  const alias = isValidPlayerAlias(p.user_alias);
  if (alias) return alias;
  const first = trimPlayerText(p.user_first_name);
  const last = trimPlayerText(p.user_last_name);
  const full = [first, last].filter(Boolean).join(" ").trim();
  if (full) return full;
  if (first) return first;
  if (last) return last;
  const pid = trimPlayerText(p.player_id);
  return pid || fallback;
}

export function playerPresenceInitials(p, fallback = "P") {
  if (!p || typeof p !== "object") return fallback;
  const alias = isValidPlayerAlias(p.user_alias);
  if (alias) return alias.slice(0, 2).toUpperCase();
  const first = trimPlayerText(p.user_first_name);
  const last = trimPlayerText(p.user_last_name);
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  return fallback;
}

export function playerClassement(p, rosterEntry) {
  if (!p && !rosterEntry) return null;
  return (
    p?.classment ||
    p?.classement ||
    rosterEntry?.classment ||
    rosterEntry?.classement ||
    null
  );
}

export function playerRankingPoints(p, rosterEntry) {
  if (!p && !rosterEntry) return null;
  const raw = p?.ranking_points ?? rosterEntry?.ranking_points;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : raw;
}

/** "A2 · 1350 pts" style label for live table UI. */
export function formatPlayerStats(classement, rankingPoints, t) {
  const parts = [];
  if (classement) parts.push(String(classement));
  if (rankingPoints != null && rankingPoints !== "") {
    const n = Number(rankingPoints);
    const pts = Number.isFinite(n) ? Math.round(n) : rankingPoints;
    parts.push(`${pts} ${t("points")}`);
  }
  return parts.length ? parts.join(" · ") : null;
}


export function formatPlayerName(p, roster, playerId = p?.player_id) {
  const fields = resolvePlayerDisplayFields(p, roster, playerId);
  return (
    fields.name ||
    (playerId || p?.player_id ? String(playerId || p.player_id).slice(-6) : "—")
  );
}

export function rosterLookup(roster, playerId) {
  if (!playerId || !Array.isArray(roster)) return null;
  return roster.find((r) => String(r.player_id) === String(playerId)) || null;
}

/**
 * Prefer live session roster over embedded table/snapshot player rows (alias can be stale).
 */
export function resolvePlayerDisplayFields(embedded, roster, playerId) {
  const pid =
    playerId ??
    embedded?.player_id ??
    embedded?.playerId ??
    null;
  const rosterEntry = rosterLookup(roster, pid);
  return {
    playerId: pid ?? rosterEntry?.player_id ?? null,
    userId: rosterEntry?.user_id ?? embedded?.user_id ?? embedded?.userId ?? null,
    name: playerDisplayName(rosterEntry) || playerDisplayName(embedded),
    classement: playerClassement(rosterEntry, embedded),
    rankingPoints: playerRankingPoints(rosterEntry, embedded),
  };
}

function formatTableSlotPlayer(slotPlayer, roster) {
  const fields = resolvePlayerDisplayFields(slotPlayer, roster, slotPlayer?.player_id);
  return {
    playerId: fields.playerId,
    userId: fields.userId,
    name: fields.name,
    classement: fields.classement,
    rankingPoints: fields.rankingPoints,
  };
}

const TABLE_SLOT_SIDE = {
  player1: "A",
  player2: "B",
  player3: "C",
};

/** Build roster rows from groupTable / socket slots — player1=A, player2=B (not stale seq ids). */
function buildTableSlotPlayers(table, roster, seq) {
  const players = [];
  for (const key of ["player1", "player2", "player3"]) {
    const slot = table?.[key];
    if (!slot) continue;
    const side = TABLE_SLOT_SIDE[key] || null;
    const roleFromSlot =
      key === "player1"
        ? seq?.player_a_role
        : key === "player2"
        ? seq?.player_b_role
        : null;
    players.push({
      ...formatTableSlotPlayer(slot, roster),
      side,
      role: roleFromSlot || null,
    });
  }
  return players;
}

function dedupeTablePlayers(players) {
  const seen = new Set();
  return players.filter((p) => {
    if (!p.playerId) return Boolean(p.name);
    const key = String(p.playerId);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isViewerPlayer(player, viewerPlayerId, viewerUserId) {
  const pid = viewerPlayerId != null ? String(viewerPlayerId) : null;
  const uid = viewerUserId != null ? String(viewerUserId) : null;
  return (
    (pid && player?.playerId != null && String(player.playerId) === pid) ||
    (uid && player?.userId != null && String(player.userId) === uid)
  );
}

/**
 * Full table context for the viewer: table number, classement, and all players
 * assigned to the same exercise table (from groupTable + sequence + roster).
 */
export function resolveViewerTableContext(
  tables,
  viewerPlayerId,
  viewerUserId,
  exercise,
  roster
) {
  let tableNumber = exercise?.table_number ?? null;
  let players = [];

  if (Array.isArray(tables)) {
    for (const table of tables) {
      let hasViewer = false;
      for (const key of ["player1", "player2", "player3"]) {
        const slot = table?.[key];
        if (!slot) continue;
        const formatted = formatTableSlotPlayer(slot, roster);
        if (isViewerPlayer(formatted, viewerPlayerId, viewerUserId)) {
          hasViewer = true;
          break;
        }
      }
      if (hasViewer) {
        tableNumber = table.table_number ?? tableNumber;
        const seq = pickSequenceForViewerTable(exercise, tableNumber);
        players = buildTableSlotPlayers(table, roster, seq);
        break;
      }
    }
  }

  const seq = pickSequenceForViewerTable(exercise, tableNumber);
  if (tableNumber == null) {
    tableNumber = seq?.table_number ?? tableNumber;
  }

  if (players.length === 0 && seq) {
    const fromSeq = [
      { id: seq.player_a_id, side: "A", role: seq.player_a_role },
      { id: seq.player_b_id, side: "B", role: seq.player_b_role },
    ].filter(
      (row) => row.id != null && isPlayerPresentInRoster(row.id, roster)
    );
    players = fromSeq.map(({ id, side, role }) => {
      const fields = resolvePlayerDisplayFields(null, roster, id);
      return {
        playerId: fields.playerId,
        userId: fields.userId,
        name: fields.name,
        classement: fields.classement,
        rankingPoints: fields.rankingPoints,
        side,
        role,
      };
    });
  }

  players = dedupeTablePlayers(players)
    .filter((p) => p.playerId || p.name)
    .map((p) => ({
      ...p,
      isViewer: isViewerPlayer(p, viewerPlayerId, viewerUserId),
    }));

  const viewer = players.find((p) => p.isViewer) || null;
  const mates = players.filter((p) => !p.isViewer);

  return {
    tableNumber,
    viewer,
    mates,
    players,
  };
}

/**
 * Club-admin style table phone: the device is pinned to `tableNumber`.
 * Players shown (and later recorded) are whoever is seated there now.
 */
export function resolvePinnedTableContext(
  tables,
  tableNumber,
  exercise,
  roster,
  viewerPlayerId,
  viewerUserId
) {
  const n = Number(tableNumber);
  if (!Number.isFinite(n) || n <= 0) return null;
  const table = (Array.isArray(tables) ? tables : []).find(
    (t) => Number(t?.table_number) === n
  );
  const seq = pickSequenceForViewerTable(exercise, n);
  let players = table ? buildTableSlotPlayers(table, roster, seq) : [];
  if (players.length === 0 && Array.isArray(table?.players)) {
    players = table.players
      .map((slot) => formatTableSlotPlayer(slot, roster))
      .filter((p) => p.playerId || p.name);
  }
  players = dedupeTablePlayers(players)
    .filter((p) => p.playerId || p.name)
    .map((p, idx) => ({
      ...p,
      side: p.side || ["A", "B", "C", "D"][idx] || null,
      isViewer: isViewerPlayer(p, viewerPlayerId, viewerUserId),
    }));
  const viewer = players.find((p) => p.isViewer) || null;
  return {
    tableNumber: n,
    viewer,
    mates: players.filter((p) => !p.isViewer),
    players,
    tablePhone: true,
  };
}

/**
 * Seated players on each table a station camera will record.
 * `tables` should already be limited to that training station.
 */
export function listTablesWithSeatedPlayers(
  tables,
  exercise,
  roster,
  viewerPlayerId,
  viewerUserId
) {
  const rows = [];
  for (const table of Array.isArray(tables) ? tables : []) {
    const n = Number(table?.table_number ?? table?.tableNumber);
    if (!Number.isFinite(n) || n <= 0) continue;
    const ctx = resolvePinnedTableContext(
      [table],
      n,
      exercise,
      roster,
      viewerPlayerId,
      viewerUserId
    );
    rows.push({
      tableNumber: n,
      tableId: table?.table_id ?? table?.tableId ?? null,
      players: (ctx?.players || []).filter((p) => p?.name),
    });
  }
  rows.sort((a, b) => a.tableNumber - b.tableNumber);
  return rows;
}

/** Find table_number from groupTable rows where the viewer is assigned. */
export function findViewerTableNumber(tables, viewerPlayerId, viewerUserId) {
  if (!Array.isArray(tables)) return null;
  const pid = viewerPlayerId != null ? String(viewerPlayerId) : null;
  const uid = viewerUserId != null ? String(viewerUserId) : null;

  for (const table of tables) {
    for (const slot of ["player1", "player2", "player3"]) {
      const p = table?.[slot];
      if (!p) continue;
      const matchesPlayer = pid && p.player_id != null && String(p.player_id) === pid;
      const matchesUser = uid && p.user_id != null && String(p.user_id) === uid;
      if (matchesPlayer || matchesUser) {
        const n = table.table_number;
        return n != null && n !== "" ? n : null;
      }
    }
  }
  return null;
}

/** Index of the current sequence step within a sorted sequence list. */
function resolveCurrentSequenceIndex(exercise, seqs) {
  const cs = exercise?.currentSequence;
  if (cs && typeof cs === "object") {
    const byTes =
      cs.tes_id != null
        ? seqs.findIndex((s) => String(s.tes_id) === String(cs.tes_id))
        : -1;
    const byOrder =
      cs.sequence_order != null
        ? seqs.findIndex((s) => s.sequence_order === cs.sequence_order)
        : -1;
    return byTes >= 0 ? byTes : byOrder >= 0 ? byOrder : 0;
  }
  if (typeof cs === "number") {
    const byOrder = seqs.findIndex((s) => s.sequence_order === cs);
    return byOrder >= 0 ? byOrder : Math.max(0, Math.min(seqs.length - 1, cs));
  }
  return 0;
}

/** Pick the sequence row most relevant to the viewer (current, or first). */
export function pickViewerSequence(exercise) {
  const seqs = [...(exercise?.sequences || [])].sort(
    (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
  );
  if (!seqs.length) return null;

  const idx = resolveCurrentSequenceIndex(exercise, seqs);
  return seqs[idx] || seqs[0];
}

/**
 * Sequence row to use for A/B roles on this table at the current step.
 * Mirrors TableSetup resolveSequenceRowForTableRoles.
 */
export function resolveSequenceRowForTableRoles(
  exercise,
  tableNumberStr,
  currentIndex0
) {
  const seqs = exercise?.sequences;
  if (!Array.isArray(seqs) || seqs.length === 0) return null;
  const idx =
    typeof currentIndex0 === "number" &&
    currentIndex0 >= 0 &&
    currentIndex0 < seqs.length
      ? currentIndex0
      : 0;
  const atIdx = seqs[idx];
  if (!atIdx) return null;
  const tn = atIdx.table_number;
  if (tn == null || tn === "" || String(tn) === String(tableNumberStr)) {
    return atIdx;
  }
  const order = atIdx.sequence_order;
  if (order != null && order !== undefined) {
    const forTable = seqs.find(
      (s) =>
        s &&
        s.sequence_order === order &&
        s.table_number != null &&
        String(s.table_number) === String(tableNumberStr)
    );
    if (forTable) return forTable;
  }
  return atIdx;
}

/** Protocol v2: per-table lane for the current sequence step. */
export function pickRolesFromPartitionMeta(meta, tableNumberStr) {
  const rows = meta?.current_sequences_by_table;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const match = rows.find(
    (r) => r != null && String(r.table_number) === String(tableNumberStr)
  );
  if (!match) return null;
  const a = match.player_a_role ?? null;
  const b = match.player_b_role ?? null;
  if (a == null && b == null) return null;
  return { player_a_role: a, player_b_role: b };
}

/** Prefer server exercise_slot_roles when present on socket payloads. */
export function pickLiveRolesFromSocketPayload(data) {
  const r = data?.exercise_slot_roles;
  if (r?.by_player_position) {
    return {
      player_a_role: r.by_player_position.Player1 ?? data.player_a_role ?? null,
      player_b_role: r.by_player_position.Player2 ?? data.player_b_role ?? null,
    };
  }
  const slots = r?.slots;
  if (Array.isArray(slots) && slots.length >= 2) {
    const a = slots.find((s) => s.side === "A");
    const b = slots.find((s) => s.side === "B");
    return {
      player_a_role: a?.role ?? data.player_a_role ?? null,
      player_b_role: b?.role ?? data.player_b_role ?? null,
    };
  }
  return {
    player_a_role: data?.player_a_role ?? null,
    player_b_role: data?.player_b_role ?? null,
  };
}

export function mergeSocketSequenceMeta(cur, payload) {
  if (!payload) return cur || {};
  const next = { ...(cur || {}) };
  if (payload.protocol_revision != null) {
    next.protocol_revision = payload.protocol_revision;
  }
  if (payload.sequence_partitioned != null) {
    next.sequence_partitioned = !!payload.sequence_partitioned;
  }
  if (Array.isArray(payload.current_sequences_by_table)) {
    next.current_sequences_by_table = payload.current_sequences_by_table;
  }
  if (payload.coach_warnings != null) {
    next.coach_warnings = Array.isArray(payload.coach_warnings)
      ? payload.coach_warnings
      : [];
  }
  return next;
}

export function payloadHasSequenceMeta(payload) {
  if (!payload) return false;
  return (
    payload.protocol_revision != null ||
    payload.sequence_partitioned != null ||
    payload.current_sequences_by_table != null ||
    payload.coach_warnings != null ||
    payload.exercise_slot_roles != null ||
    payload.player_a_role !== undefined ||
    payload.player_b_role !== undefined
  );
}

/** Side A/B roles for a coach table card (live socket first, then plan). */
export function resolveCoachTableSideRoles(
  exercise,
  tableNumber,
  {
    isExerciseRunning = false,
    liveRoles = null,
    sequenceMeta = null,
    currentSequenceIndex = null,
  } = {}
) {
  const tableNumberStr =
    tableNumber != null && tableNumber !== "" ? String(tableNumber) : "";

  if (isExerciseRunning) {
    const partition = pickRolesFromPartitionMeta(sequenceMeta, tableNumberStr);
    if (partition && (partition.player_a_role || partition.player_b_role)) {
      return partition;
    }
    if (liveRoles && (liveRoles.player_a_role || liveRoles.player_b_role)) {
      return liveRoles;
    }
  }

  const seqs = exercise?.sequences;
  if (Array.isArray(seqs) && seqs.length) {
    let idx = currentSequenceIndex;
    if (idx == null && exercise?.currentSequence != null) {
      const sorted = [...seqs].sort(
        (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
      );
      idx = resolveCurrentSequenceIndex(exercise, sorted);
    }
    const seq = resolveSequenceRowForTableRoles(
      exercise,
      tableNumberStr,
      idx
    );
    if (seq) {
      return {
        player_a_role: seq.player_a_role ?? null,
        player_b_role: seq.player_b_role ?? null,
      };
    }
  }

  const fallback = tableNumberStr
    ? pickSequenceForViewerTable(exercise, tableNumberStr)
    : pickViewerSequence(exercise);
  return {
    player_a_role: fallback?.player_a_role ?? null,
    player_b_role: fallback?.player_b_role ?? null,
  };
}

/**
 * Sequence row for a specific table at the current step (multi-table exercises).
 * Mirrors TableSetup resolveSequenceRowForTableRoles.
 */
export function pickSequenceForViewerTable(exercise, tableNumber) {
  const seqs = [...(exercise?.sequences || [])].sort(
    (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
  );
  if (!seqs.length) return null;

  const idx = resolveCurrentSequenceIndex(exercise, seqs);
  const atIdx = seqs[idx] || seqs[0];
  const tableNumberStr =
    tableNumber != null && tableNumber !== "" ? String(tableNumber) : null;

  if (!tableNumberStr) return atIdx;

  const tn = atIdx?.table_number;
  if (tn == null || tn === "" || String(tn) === tableNumberStr) {
    return atIdx;
  }

  const order = atIdx?.sequence_order;
  if (order != null) {
    const forTable = seqs.find(
      (s) =>
        s &&
        s.sequence_order === order &&
        s.table_number != null &&
        String(s.table_number) === tableNumberStr
    );
    if (forTable) return forTable;
  }

  return atIdx;
}

/** Derive side (A/B) and role (active/sparring) from sequence player slots. */
export function deriveViewerSlotFromSequence(exercise, viewerPlayerId, tableNumber) {
  if (!viewerPlayerId) return null;
  const seq =
    pickSequenceForViewerTable(exercise, tableNumber) || pickViewerSequence(exercise);
  if (!seq) return null;

  const pid = String(viewerPlayerId);
  if (seq.player_a_id != null && String(seq.player_a_id) === pid) {
    return { side: "A", role: seq.player_a_role || null };
  }
  if (seq.player_b_id != null && String(seq.player_b_id) === pid) {
    return { side: "B", role: seq.player_b_role || null };
  }
  return null;
}

/**
 * @param {string|null|undefined} role
 * @param {Function} t
 * @param {{ multiball?: boolean }} [opts] multiball labels the sparring side as the feeder
 */
export function normalizeExerciseRole(role, t, opts = {}) {
  if (!role) return null;
  const key = String(role).toLowerCase();
  if (opts.multiball) {
    if (key === "sparring" || key.includes("spar")) return t("liveModeRoleFeeder", "Feeder");
    if (key === "active" || key === "both" || key.includes("perform")) {
      return t("liveModeRolePerformer", "Performer");
    }
  }
  if (
    key === "active" ||
    key === "performance" ||
    key.includes("actif") ||
    key.includes("perform")
  ) {
    return t("performance_role", "Performance");
  }
  if (key === "sparring" || key.includes("spar")) return t("sparring", "Sparring");
  return role;
}

/** Message sender user_id from API/socket payload shapes. */
export function messageSenderId(msg) {
  if (!msg) return null;
  return msg.sender_id ?? msg.sender?.user_id ?? null;
}

/** Message body from API/socket payload shapes. */
export function messageBody(msg) {
  if (!msg) return "";
  return msg.text_message || msg.message_content || msg.text || "";
}
